<script>
import Common from '../../../common/Common.vue'
import Services from '../../../common/Services.vue'
import '@fullcalendar/core/vdom'
import dayGridPlugin from '@fullcalendar/daygrid'
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import enGbLocale from '@fullcalendar/core/locales/en-gb'
import { schedule } from '../../../types/schedule'
import moment from 'moment-timezone'

export default {
    init: async payload => {
        await payload.initCalendar()

        payload.isSuperAdminMaster = await Common.isSuperAdminMaster(payload)
        const role = await Common.getSubtype(payload)
        payload.role = role

        if (role === 'SUPER_ADMIN_MASTER') {
            payload.accounts = await Services.filterAccounts(payload)
        } else {
            payload.clients = await Services.filterClients(payload)
            payload.filters.account = await Common.getAccountId(payload)

            if (role === 'MANAGER' || role === 'AUDITOR') {
                const client = await Common.getClientId(payload)
                payload.filters.client = client
                payload.sites = await Services.getSites(payload)
            }
        }

        payload.isScopeReady = true
        if (payload.visibleCalendarRange) {
            await payload.fetchVisibleRange()
        }
    },
    methods: {
        getCalendarScopeKey(filters) {
            return JSON.stringify(['account', 'client', 'site', 'startDate', 'endDate'].map(field => filters?.[field] || ''))
        },
        mapCalendarEvents(items) {
            return (items || []).map(item => ({
                ...item,
                id: String(item._id || item.id || ''),
                ...(item.start || item.startDate ? { start: item.start || item.startDate, allDay: false } : {}),
                ...(item.end || item.endDate ? { end: item.end || item.endDate } : {}),
            }))
        },
        async getAppointments() {
            if (this.calendarDisposed) return false
            const requestId = (this.appointmentsRequestId || 0) + 1
            this.appointmentsRequestId = requestId

            if (this.role === 'SUPER_ADMIN_MASTER' && !this.filters.account) {
                this.isLoading = false
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.select.account.required'))
                return false
            }

            this.isLoading = true

            try {
                const filters = { ...this.filters }
                const scopeKey = this.getCalendarScopeKey(filters)
                const items = await Services.getAppointmentsByDate(this, filters, { rejectOnError: true })

                if (requestId !== this.appointmentsRequestId) return false

                const events = this.mapCalendarEvents(items)
                this.appointments = events
                this.calendarOptions = {
                    ...this.calendarOptions,
                    events,
                }
                this.syncCalendarEvents(events)
                this.calendarLoadedScopeKey = scopeKey
                this.calendarLoadError = false
                return true
            } catch (_error) {
                if (requestId === this.appointmentsRequestId) {
                    this.calendarLoadError = true
                    this.visibleRangeLoadedKey = null
                }
                return false
            } finally {
                if (requestId === this.appointmentsRequestId) {
                    this.isLoading = false
                }
            }
        },
        buildScheduleDraft(beginDate) {
            const scheduleObj = JSON.parse(JSON.stringify(schedule))

            scheduleObj.beginDate = beginDate
            scheduleObj.account = this.filters.account
            scheduleObj.client = this.filters.client
            scheduleObj.site = this.filters.site

            return scheduleObj
        },
        syncCalendarEvents(items) {
            const calendarApi = this.$refs?.calendar?.getApi?.()
            if (!calendarApi) {
                this.calendarSyncStatus = 'not-mounted'
                return false
            }

            calendarApi.removeAllEvents()
            calendarApi.addEventSource(items || [])
            this.calendarSyncStatus = 'synchronized'
            return true
        },
        async openCreateScheduleModal(beginDate) {
            const selectedDate = typeof beginDate === 'string' && beginDate
                ? beginDate
                : moment().tz(Common.getAccountTimezone()).format('YYYY-MM-DD')

            this.selectedDate = selectedDate
            this.newSchedule = this.buildScheduleDraft(selectedDate)

            this.$bvModal.show('createScheduleModal')
        },
        async fetchVisibleRange() {
            const { startStr, endStr } = this.visibleCalendarRange || {}
            if (!startStr || !endStr) return

            const rangeKey = `${startStr}|${endStr}`
            if (this.visibleRangeLoadedKey === rangeKey) return true
            if (this.visibleRangeInFlightKey === rangeKey) return this.visibleRangeInFlightPromise

            this.filters.startDate = moment(startStr).utc().format()
            this.filters.endDate = moment(endStr).utc().format()

            this.visibleRangeInFlightKey = rangeKey
            const request = this.getAppointments()
            this.visibleRangeInFlightPromise = request

            try {
                const loaded = await request
                if (loaded) this.visibleRangeLoadedKey = rangeKey
                return loaded
            } finally {
                if (this.visibleRangeInFlightKey === rangeKey) {
                    this.visibleRangeInFlightKey = null
                    this.visibleRangeInFlightPromise = null
                }
            }
        },
        async handleDatesSet({ startStr, endStr }) {
            this.visibleCalendarRange = { startStr, endStr }
            this.filters.startDate = moment(startStr).utc().format()
            this.filters.endDate = moment(endStr).utc().format()

            if (this.isScopeReady) {
                await this.fetchVisibleRange()
            }
        },
        initCalendar: async function () {
            const language = this.$session.get('user')?.language

            const locale = language === 'pt' ? 'pt-br' : 'en-gb'

            this.calendarOptions = {
                timeZone: Common.getAccountTimezone(),
                plugins: [momentTimezonePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, bootstrapPlugin],
                locales: [ptBrLocale, enGbLocale],
                locale: locale,
                headerToolbar: {
                    left: 'dayGridMonth,timeGridWeek,timeGridDay',
                    center: 'title',
                    right: 'prev,next today',
                },
                buttonText: {
                    today: this.$t('str.today'),
                    month: this.$t('str.month'),
                    week: this.$t('str.week'),
                    day: this.$t('str.day'),
                },
                dateClick: this.handleDateClick,
                eventClick: this.handleEventClick,
                datesSet: dateInfo => this.handleDatesSet(dateInfo),
                initialView: 'dayGridMonth',
                droppable: false,
                editable: false,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                themeSystem: 'bootstrap',
                events: [],
                views: {
                    timeGrid: {
                        eventLimit: 6,
                    },
                },
            }
        },
        changeLanguage: function () {
            const language = this.$session.get('user')?.language
            const locale = language === 'pt' ? 'pt-br' : 'en-gb'

            this.calendarOptions.locale = locale
        },
        handleDateClick: function (arg) {
            if (this.role === 'AUDITOR') return
            this.selectedDate = moment.tz(arg.dateStr, Common.getAccountTimezone()).format('YYYY-MM-DD')
            return this.openCreateScheduleModal(this.selectedDate)
        },
        handleEventClick: async function (arg) {
            const appointment = this.appointments.find(appointment => String(appointment._id || appointment.id || '') === arg?.event?.id)
            if (!appointment?.schedule || !arg?.event?.id) return
            const referenceId = value => value?._id || value
            const filters = {
                account: referenceId(appointment.account),
                client: referenceId(appointment.client),
                site: referenceId(appointment.site),
                schedule: referenceId(appointment.schedule),
            }

            const selectedAppointment = await Services.getVigilantScheduleById(this, filters)
            if (!selectedAppointment) return

            selectedAppointment.appointment = appointment
            this.selectedAppointment = selectedAppointment

            this.$bvModal.show('createScheduleModal')
        },
        changeAccount: async function () {
            if (this.calendarDisposed) return
            const requestId = (this.clientsRequestId || 0) + 1
            this.clientsRequestId = requestId
            this.sitesRequestId = (this.sitesRequestId || 0) + 1
            const account = this.filters.account
            this.filters.client = ''
            this.filters.site = ''
            this.clients = []
            this.sites = []
            this.getAppointments()
            const clients = await Services.getClientsByAccount(this, account)
            if (!this.calendarDisposed && this.clientsRequestId === requestId && this.filters.account === account) this.clients = clients
        },
        changeClient: async function () {
            if (this.calendarDisposed) return
            const requestId = (this.sitesRequestId || 0) + 1
            this.sitesRequestId = requestId
            const account = this.filters.account
            const client = this.filters.client
            this.filters.site = ''
            this.sites = []
            this.getAppointments()
            const sites = await Services.getSitesByClient(this, client)
            if (!this.calendarDisposed && this.sitesRequestId === requestId && this.filters.account === account && this.filters.client === client) this.sites = sites
        },
        changeSite: async function () {
            this.getAppointments()
        },
    },
}
</script>
